/**
 * Scripted product tour — Mi6 AIP walk-through.
 * 8 stops. Presenter clicks Next manually to control pacing.
 * Reset at the end restores Tenant 1 accounts to their starting state.
 *
 * W36-C: No demo company names; all example references use neutral labels.
 * Updated 2026-05-07.
 */

export type TourRole = "ae" | "manager" | "bdr" | "executive" | "any";

export interface TourStep {
  id: string;
  title: string;
  route: string;                  // hash route, e.g. "/#/dashboards/manager"
  role?: TourRole;
  targetSelector: string;         // CSS selector or [data-tour="..."]
  narration: string;
  whyItMatters: string;
  expectedAction?: string;
  autoAdvanceOn?: "click" | "route";
  isResetStop?: boolean;          // marks the final reset stop
}

export const TOUR_STEPS: TourStep[] = [
  {
    id: "manager-dashboard",
    title: "Stop 1 — Manager dashboard",
    route: "/#/dashboards/manager",
    role: "manager",
    targetSelector: '[data-tour="account-grid"]',
    narration:
      "This is what a sales manager sees Monday morning. Every active deal, every checkpoint, every score — in one grid. Red cells are blocked. Green cells are clean. The manager starts here, not in their inbox.",
    whyItMatters:
      "Pipeline health at a glance before the first call of the day.",
  },
  {
    id: "ae-pipeline",
    title: "Stop 2 — AE pipeline",
    route: "/#/dashboards/ae",
    role: "ae",
    targetSelector: '[data-tour="ae-hero-tiles"]',
    narration:
      "Now drill into an AE view. Quota, weighted forecast, attainment, and accounts that need attention. The AE uses this to prioritise which deals to work today and which to move.",
    whyItMatters:
      "Three numbers replace a 30-minute pipeline review.",
  },
  {
    id: "helix-viz",
    title: "Stop 3 — Helix 10-Checkpoint scoring",
    route: "/#/helix-demo",
    role: "any",
    targetSelector: '[data-tour="helix-score-viz"]',
    narration:
      "The Helix score. 10 Checkpoints (CP1–CP10) — CP1 Project, CP2 Need / Pain / Demonstration, CP3 Budget, CP4 Timeline, CP5 Authority — Project / Initiative, CP6 Technical and Financial Sign-off, CP7 Org Structure and Influence Map, CP8 Competitive Position, CP9 Compliance — IT Internal and Industry-Specific, CP10 Legal — Contracts, SLAs, Terms. Each Checkpoint is green, orange, or red. The score is live. No subjectivity.",
    whyItMatters:
      "One number that tells the whole story of a deal's qualification status.",
  },
  {
    id: "question-tree",
    title: "Stop 4 — Question tree",
    route: "/#/question-tree",
    role: "any",
    targetSelector: '[data-tour="question-tree"]',
    narration:
      "The question tree shows how answers cascade. Each checkpoint resolves to a small set of canonical questions. Canonical, I-think, TBD, skip — every answer is a chip. The questions drive the score.",
    whyItMatters:
      "Same questions for every deal. Same standard for every AE. No guesswork.",
  },
  {
    id: "decision-tree",
    title: "Stop 5 — Decision tree",
    route: "/#/decision-tree",
    role: "any",
    targetSelector: '[data-tour="decision-tree-root"]',
    narration:
      "The decision tree shows the routing logic. Depending on what the AE has answered, the system routes them to the next required question or flags a hard-kill checkpoint. Deals can't skip past blockers.",
    whyItMatters:
      "Qualification is enforced by logic, not by manager memory.",
  },
  {
    id: "bdr-queue",
    title: "Stop 6 — BDR queue",
    route: "/#/dashboards/bdr",
    role: "bdr",
    targetSelector: '[data-tour="bdr-queue"]',
    narration:
      "The BDR view shows the cadence engine. Hunt items in priority order — source, age, last touch, next move. The BDR works the queue. The queue works the pipeline.",
    whyItMatters:
      "No more spreadsheets. No more missed follow-ups.",
  },
  {
    id: "marketing-funnel",
    title: "Stop 7 — Marketing funnel",
    route: "/#/dashboards/marketing",
    role: "any",
    targetSelector: '[data-tour="marketing-source-split"]',
    narration:
      "The marketing view shows source-split outcomes. Which sources convert. Which campaigns generate qualified pipeline. Marketing and sales share the same data model.",
    whyItMatters:
      "Marketing budget decisions backed by qualified pipeline data, not vanity metrics.",
  },
  {
    id: "demo-reset",
    title: "Stop 8 — Reset for next demo",
    route: "/#/dashboards/manager",
    role: "any",
    targetSelector: '[data-tour="demo-reset-button"]',
    narration:
      "That completes the walk-through. Use the Reset button below to restore all accounts to their starting state before the next session.",
    whyItMatters:
      "Every prospect sees a clean, consistent demo.",
    isResetStop: true,
  },
];
